import type { Translate } from 'next-translate';
import Trans from 'next-translate/Trans';
import Link from 'next/link';
import React from 'react';
import { type DropzoneOptions, type FileRejection } from 'react-dropzone';
import { Container } from '@/components/elements/Container';
import { DropzoneWrapper } from '@/components/elements/DropzoneWrapper';
import { MyImage } from '@/components/elements/MyImage';
import { VideoLoadingOverlay } from '@/components/elements/VideoLoadingOverlay';
import { Button } from '@/components/UI/Button';
import {
	CookiesStorageService,
	GenerateLandingLinkService,
	GoogleService,
} from '@/services';
import {
	getDropzoneErrorMessage,
	getMediaDuration,
	niceBytes,
} from '@/helpers';
import { type HeroSpeechToText as HeroSpeechToTextData } from '@/types';
import {
	GOOGLE_EVENTS,
	LANDING_CONSTANTS,
	LANDING_COOKIE_STORAGE,
} from '@/constants';
import AddFileIcon from '@/public/icons/add-file.svg';
import bgMobile from '@/public/images/landing/backgrounds/bg-abstract-mobile.webp';
import bg from '@/public/images/landing/backgrounds/bg-abstract.webp';
import youtubeIcon from '@/public/images/landing/youtube.webp';
import {
	buildSpeechToTextCookie,
	SPEECH_TO_TEXT_COOKIE_OPTIONS,
} from './speechToTextCookie';
import styles from './HeroSpeechToText.module.scss';

type SourceTab = 'file' | 'link';

const MAX_URL_LENGTH = 600;
const MAX_DURATION_HOURS = Math.round(
	LANDING_CONSTANTS.MAX_SPEECH_TO_TEXT_DURATION / 3600,
);
const TOO_LONG_ERROR = `Recordings longer than ${MAX_DURATION_HOURS} hours aren't supported.`;

interface Props {
	data: HeroSpeechToTextData;
	t: Translate;
	dropzoneOptions: DropzoneOptions;
	isLoading: boolean;
	uploadPercentage?: number;
	funnelLinkService?: string;
}

export const HeroSpeechToText: React.FC<Props> = ({
	data,
	t,
	dropzoneOptions,
	isLoading,
	uploadPercentage = 0,
	funnelLinkService,
}) => {
	const { title, description, buttonText, background, data: info } = data;

	const [activeTab, setActiveTab] = React.useState<SourceTab>('file');
	const [link, setLink] = React.useState('');
	const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

	const maxSize = niceBytes({
		bytes: dropzoneOptions.maxSize ?? LANDING_CONSTANTS.MAX_VIDEO_FILE_SIZE,
		decimalNumber: 0,
	});

	// Surface rejections next to the dropzone and stop over-long recordings before
	// they upload — the funnel would reject them after the redirect anyway
	const { onDrop: uploadOnDrop, ...restDropzoneOptions } = dropzoneOptions;
	const handleDrop = React.useCallback<NonNullable<DropzoneOptions['onDrop']>>(
		(accepted, rejections: FileRejection[], event) => {
			const rejectionMessage = getDropzoneErrorMessage(rejections, {
				maxSize: `Unfortunately, the maximum file size must not exceed ${maxSize}.`,
				invalidType: 'Unsupported file format.',
			});
			setErrorMessage(rejectionMessage);

			const file = accepted[0];
			if (rejectionMessage || !file) return;

			void getMediaDuration(file).then(duration => {
				if (
					duration !== null &&
					duration > LANDING_CONSTANTS.MAX_SPEECH_TO_TEXT_DURATION
				) {
					setErrorMessage(TOO_LONG_ERROR);
					return;
				}
				uploadOnDrop?.(accepted, rejections, event);
			});
		},
		[maxSize, uploadOnDrop],
	);
	const fileDropzoneOptions: DropzoneOptions = React.useMemo(
		() => ({ ...restDropzoneOptions, disabled: isLoading, onDrop: handleDrop }),
		[restDropzoneOptions, isLoading, handleDrop],
	);

	const goToFunnel = () => {
		GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);
		window.location.href =
			funnelLinkService || GenerateLandingLinkService.webApp();
	};

	const handleGenerate = () => {
		const trimmedLink = link.trim();
		// The funnel validates the link itself; the landing only hands it over
		if (activeTab === 'link' && trimmedLink) {
			CookiesStorageService.set(
				LANDING_COOKIE_STORAGE.SPEECH_TO_TEXT_FROM_LANDING,
				buildSpeechToTextCookie('youtube', trimmedLink),
				SPEECH_TO_TEXT_COOKIE_OPTIONS,
			);
		}
		goToFunnel();
	};

	const selectTab = (tab: SourceTab) => {
		setActiveTab(tab);
		setErrorMessage(null);
	};

	return (
		<section className={styles['hero']}>
			{background && (
				<MyImage
					imageProps={{
						src: bgMobile,
						alt: 'background image',
						title: `${t(title)}`,
						priority: true,
						fetchPriority: 'low',
					}}
					settings={{
						classes: {
							picture: styles['hero__background'],
							image: styles['hero__background-img'],
						},
					}}
				>
					<source srcSet={bg.src} media='(min-width: 1023px)' />
				</MyImage>
			)}

			<Container className={styles['hero__container']}>
				<div className={styles['hero__info']}>
					<h1 className={styles['hero__title']}>{t(title)}</h1>
					<p className={styles['hero__subtitle']}>{t(description)}</p>
				</div>

				<div className={styles['hero__panel']}>
					<div className={styles['hero__tabs']} role='tablist'>
						<button
							type='button'
							role='tab'
							aria-selected={activeTab === 'file'}
							className={`${styles['hero__tab']} ${
								activeTab === 'file' ? styles['hero__tab_active'] : ''
							}`}
							onClick={() => selectTab('file')}
						>
							<span className={styles['hero__tab-emoji']} aria-hidden='true'>
								🎙️
							</span>
							{t(info.fileTab)}
						</button>
						<button
							type='button'
							role='tab'
							aria-selected={activeTab === 'link'}
							className={`${styles['hero__tab']} ${
								activeTab === 'link' ? styles['hero__tab_active'] : ''
							}`}
							onClick={() => selectTab('link')}
						>
							<MyImage
								imageProps={{
									src: youtubeIcon,
									alt: '',
									title: t(info.linkTab),
								}}
								settings={{
									classes: {
										picture: styles['hero__tab-youtube'],
										image: styles['hero__tab-youtube-img'],
									},
								}}
							/>
							{t(info.linkTab)}
						</button>
					</div>

					{activeTab === 'file' ? (
						<DropzoneWrapper
							options={fileDropzoneOptions}
							classes={{
								dropzoneClass: `${styles['hero__field']} ${
									styles['hero__field_dropzone']
								} ${isLoading ? styles['hero__field_disabled'] : ''}`,
							}}
						>
							{({ isDragActive, fileRejections }) => (
								<div
									className={`${styles['hero__field-content']} ${
										isDragActive ? styles['hero__field-content_active'] : ''
									} ${
										fileRejections.length > 0
											? styles['hero__field-content_reject']
											: ''
									}`}
								>
									<AddFileIcon className={styles['hero__field-icon']} />
									<p className={styles['hero__field-title']}>
										{t(info.dropzoneTitle)}
									</p>
									<div className={styles['hero__field-meta']}>
										<span>{t(info.durationLabel)}</span>
										<span
											className={styles['hero__field-dot']}
											aria-hidden='true'
										/>
										<span>{t(info.sizeLabel)}</span>
									</div>
								</div>
							)}
						</DropzoneWrapper>
					) : (
						<label
							className={`${styles['hero__field']} ${styles['hero__field_link']}`}
						>
							<input
								type='url'
								inputMode='url'
								maxLength={MAX_URL_LENGTH}
								value={link}
								placeholder={t(info.inputPlaceholder)}
								aria-label={t(info.inputPlaceholder)}
								className={styles['hero__link-input']}
								onChange={event => setLink(event.target.value)}
							/>
						</label>
					)}

					{errorMessage && (
						<p className={styles['hero__error']} role='alert'>
							{errorMessage}
						</p>
					)}
				</div>

				<div className={styles['hero__button-wrapper']}>
					<Button
						as='button'
						preset='landing'
						text={t(buttonText)}
						size='large'
						loading={isLoading}
						classes={{ button: styles['hero__button'] }}
						onClick={handleGenerate}
					/>
				</div>

				<span className={styles['hero__agree']}>
					<Trans
						i18nKey='common:agreeUploadFile'
						components={{
							terms: <Link href='/terms-of-use' target='_blank' />,
							privacy: <Link href='/privacy-policy' target='_blank' />,
						}}
					/>
				</span>

				<div className={styles['hero__trust-badges']}>
					<span className={styles['hero__trust-badge']}>
						<span aria-hidden='true'>⭐️</span>
						{t(info.trustBadge1)}
					</span>
					<span className={styles['hero__trust-badge']}>
						<span aria-hidden='true'>⭐️</span>
						{t(info.trustBadge2)}
					</span>
				</div>

				{isLoading && <VideoLoadingOverlay percentage={uploadPercentage} />}
			</Container>
		</section>
	);
};
